import { fetchProtectedFile } from "../libraries/fileAccess";
import { optionFilterCellEmpty } from "@lhd/components/ReactGridCustom/utils";
import {
  DATE_TIME_FORMAT,
  IMAGE_FILE_EXTENSIONS,
  OFFICE_FILE_EXTENSIONS,
  REGEX,
  VIDEO_FILE_EXTENSIONS,
} from "@lhd/constants";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { fieldsDefined } from "@lhd/features/Customers/NewCustomerControl/utils";
import {
  MONTHLY,
  INSURANCE_OPTIONS,
  INHABITANT_TAX_TYPE,
} from "@lhd/features/Salary/StaffTaxSheets/StaffTaxSheetsList/utility";
import {
  CellChange,
  CheckboxCell,
  DateCell,
  DropdownCell,
  NumberCell,
  TextCell,
} from "@silevis/reactgrid";
import dayjs, { Dayjs } from "dayjs";
import {
  cloneDeep,
  get,
  isEmpty,
  isNil,
  orderBy,
  pick,
  isNumber,
  isString,
  omit,
  toString,
} from "lodash";
import { getCookie } from "./cookie";
import moment from "moment";

export * from "./sort";
export * from "./PermissionUtils";
export * from "./filterNullTokens";
export * from "./displayMode";
export * from "./cookie";
export * from "./getTabTitle";
export * from "./formatSignedNumber";
export * from "./columnConfigSettings";
export * from "./triggerWindowResize";

export const getAccessToken = () => getCookie("access_token") || "";
export const getClient = () => localStorage.getItem("client") || "";
export const getUid = () => localStorage.getItem("uid") || "";
export const getCompanyIdSession = () =>
  sessionStorage.getItem("company_id") || "";

export const getCompanyId = () =>
  getCompanyIdSession() || localStorage.getItem("company_id") || "";

export const setCompanyId = (id: string) => {
  localStorage.setItem("company_id", id);
  sessionStorage.setItem("company_id", id);
};

export const getExpiry = () => localStorage.getItem("expiry") || "";
export const getRemember = () => localStorage.getItem("remember") || false;
export const PRE_KEY_DEFAULT_YEAR_SELECTED = "default_year_selected_";
export const getDefaultYearSelected = (key: string) =>
  sessionStorage.getItem(key);
export const setDefaultYearSelected = (key: string, year: string) => {
  sessionStorage.setItem(key, year);
};

export const toComponentName = (str?: string) => {
  if (str) {
    return str
      .split("_")
      .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
      .join("");
  }
  return "";
};

export const checkUppercase = (str: string | null | undefined) => {
  if (str)
    for (let i = 0; i < str.length; i++) {
      if (
        str.charAt(i) == str.charAt(i).toUpperCase() &&
        str.charAt(i).match(/[a-z]/i)
      ) {
        return true;
      }
    }
  return false;
};

export const hasWhiteSpace = (s: string | null | undefined) => {
  if (s) {
    const reWhiteSpace = s.split(" ");
    if (reWhiteSpace.length > 1) {
      return false;
    }
  }
  return true;
};

export const randomBackgroundColor = () => {
  const x = Math.floor(Math.random() * 256);
  const y = Math.floor(Math.random() * 256);
  const z = Math.floor(Math.random() * 256);

  return "rgb(" + x + "," + y + "," + z + ")";
};

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("ja-JP").format(value);
};

export const formatDateTimeJP = (
  value: any,
  picker?: string,
  format?: string,
) => {
  if (picker === "month") {
    return moment(value).format(DATE_TIME_FORMAT.MONTH_JP);
  } else if (picker === "month-year") {
    return moment(value).format(DATE_TIME_FORMAT.MONTH_YEAR_JP);
  } else {
    return moment(value).format(format ? format : DATE_TIME_FORMAT.DATE_JP);
  }
};

export const checkDateInValid = (value: any) => {
  const dateEn = moment(value, DATE_TIME_FORMAT.DATE, true).isValid();
  const dateJp: any = moment(value, DATE_TIME_FORMAT.DATE_JP, true).isValid();

  return dateEn
    ? value
    : dateJp
    ? moment(value, DATE_TIME_FORMAT.DATE).format(DATE_TIME_FORMAT.DATE)
    : value;
};

export const checkWorkingTypeValid = (value: any) => {
  return value === "clear" ? null : value;
};

export const formatZipCode = (value: string) => {
  if (!value) return "";
  return value.length <= 7
    ? value.replace(/^(\d{3})(\d{4})/, "$1-$2")
    : value.replace(/^(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
};

export const formatTel = (value: string) => {
  if (!value) return "";
  return value.replace(/^(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
};

export const numberWithCommas = (value: number) => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const makeId = (length = 5) => {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};

const isPrimitive = (o: any) => {
  switch (typeof o) {
    case "object": {
      return false;
    }
    case "function": {
      return false;
    }
    default: {
      return true;
    }
  }
};
const typeOf = (o: any) => Object.prototype.toString.call(o);
const isObject = (o: any) =>
  o !== null &&
  o !== "" &&
  !Array.isArray(o) &&
  typeOf(o).split(" ")[1].slice(0, -1) === "Object";

export const getChanges = (previous: any, current: any): any => {
  if (isPrimitive(previous) && isPrimitive(current)) {
    if (previous === current) {
      return "";
    }

    return current;
  }

  if (isObject(previous) && isObject(current)) {
    const diff = getChanges(Object.entries(previous), Object.entries(current));

    return diff.reduce((merged: any, [key, value]: any) => {
      return {
        ...merged,
        [key]: value,
      };
    }, {});
  }

  const changes: any = [];

  if (JSON.stringify(previous) === JSON.stringify(current)) {
    return changes;
  }

  for (let i = 0; i < current.length; i++) {
    const item = current[i];

    if (JSON.stringify(item) !== JSON.stringify(previous[i])) {
      changes.push(item);
    }
  }

  return changes;
};

export const handleColumns = (columns: any) => {
  return columns.reduce((curr: any, item: any) => {
    return (curr = {
      ...curr,
      [item.columnId]: item.width,
    });
  }, {});
};

export const allKeysEmptyObjects = (obj: any) => {
  if (!obj) return true;
  return Object.values(obj).every(
    (value) =>
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0,
  );
};

// Helper function to expand filter values for insurance and inhabitant_tax_type fields
// Maps filter values to include both label and value for matching
export const expandFilterValues = (
  key: string,
  filterValues: string[],
): string[] => {
  const expandedValues = new Set<string>();

  // Add original filter values
  filterValues.forEach((val) => expandedValues.add(val?.toString()));

  // Check if key is insurance_existence_last_* field
  if (
    key === "insurance_existence_last_year" ||
    (key.startsWith("insurance_existence_last_") && key.endsWith("_month"))
  ) {
    // Map each filter value to both label and value from INSURANCE_OPTIONS
    filterValues.forEach((filterVal) => {
      const option = INSURANCE_OPTIONS.find(
        (opt) => opt.label === filterVal || opt.value === filterVal,
      );
      if (option) {
        expandedValues.add(option.label);
        expandedValues.add(option.value);
        // Also add boolean values for comparison
        expandedValues.add(option.value === "true" ? "true" : "false");
      }
      // Also handle direct boolean string values
      if (filterVal === "true" || filterVal === "false") {
        const boolOption = INSURANCE_OPTIONS.find(
          (opt) => opt.value === filterVal,
        );
        if (boolOption) {
          expandedValues.add(boolOption.label);
        }
      }
    });
  }

  // Check if key is inhabitant_tax_type field
  if (key === "inhabitant_tax_type") {
    // Map each filter value to both label and value from INHABITANT_TAX_TYPE
    filterValues.forEach((filterVal) => {
      const option = INHABITANT_TAX_TYPE.find(
        (opt) => opt.label === filterVal || opt.value === filterVal,
      );
      if (option) {
        expandedValues.add(option.label);
        expandedValues.add(option.value);
      }
    });
  }

  return Array.from(expandedValues);
};

// Helper function to normalize data value for comparison
// Converts boolean to string and maps to label/value for insurance fields
export const normalizeDataValue = (key: string, dataValue: any): string[] => {
  const normalizedValues = new Set<string>();

  // Convert to string first
  const stringValue = dataValue?.toString();

  // Check if key is insurance_existence_last_* field
  if (
    key === "insurance_existence_last_year" ||
    (key.startsWith("insurance_existence_last_") && key.endsWith("_month"))
  ) {
    // If value is boolean, convert to string and map to label/value
    if (typeof dataValue === "boolean") {
      const boolString = dataValue.toString(); // "true" or "false"
      normalizedValues.add(boolString);
      const option = INSURANCE_OPTIONS.find((opt) => opt.value === boolString);
      if (option) {
        normalizedValues.add(option.label);
      }
    } else if (stringValue) {
      // If value is already string (label or value), add it and map to the other
      normalizedValues.add(stringValue);
      const option = INSURANCE_OPTIONS.find(
        (opt) => opt.label === stringValue || opt.value === stringValue,
      );
      if (option) {
        normalizedValues.add(option.label);
        normalizedValues.add(option.value);
      }
    }
  } else {
    // For other fields, just add the string value
    if (stringValue) {
      normalizedValues.add(stringValue);
    }
  }

  return Array.from(normalizedValues);
};

export const matchDateFilter = (
  dateValue: string,
  filterValue: string,
): boolean => {
  if (!dateValue || !filterValue) return false;

  const date = isJPDateLabel(dateValue)
    ? moment(dateValue, DATE_TIME_FORMAT.DATE_JP)
    : moment(dateValue);
  if (!date.isValid()) return false;

  const filter = filterValue.toString();

  const formats = [
    date.format("YYYY年MM月DD日"),
    date.format("YYYY年MM月"),
    date.format("YYYY年"),
    date.format("YYYY/MM/DD"),
    date.format("YYYY/MM"),
    date.format("YYYY"),
  ];

  return formats.some((format) => format === filter);
};

export const flattenDataFilter = (
  obj: any,
  prefix = "",
  result: any[] = [],
) => {
  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

    const value = obj[key];
    const currentPath = prefix ? `${prefix}.${key}` : key;

    if (value === null || value === undefined) {
      continue;
    } else if (Array.isArray(value)) {
      // Handle arrays: iterate through items and use array index in path
      value.forEach((item, index) => {
        if (item === null || item === undefined) {
          return;
        }
        // Build path with array index: e.g., worker_dependants.0
        const arrayItemPath = `${currentPath}.${index}`;

        // Recursively flatten the array item
        if (
          typeof item === "object" &&
          !Array.isArray(item) &&
          // Check if it's a filter object (has search, sort, filter, etc.)
          !("search" in item || "sort" in item || "filter" in item)
        ) {
          // It's a nested object, recurse
          flattenDataFilter(item, arrayItemPath, result);
        } else {
          // It's a filter object or leaf value
          result.push({ path: arrayItemPath, value: item });
        }
      });
    } else if (
      typeof value === "object" &&
      value !== null &&
      // Check if it's a filter object (has search, sort, filter, etc.)
      !("search" in value || "sort" in value || "filter" in value)
    ) {
      // It's a nested object, recurse
      flattenDataFilter(value, currentPath, result);
    } else {
      // It's a filter object or leaf value
      result.push({ path: currentPath, value });
    }
  }
  return result;
};

export const handleDataFilter = (
  dataFilter: any,
  originalData: any,
  keys?: any,
  getValue?: (value: any, key: string) => string,
) => {
  const cloneDeepData = cloneDeep(originalData).filter((item: any) => {
    const isActive = item.isActive;
    const isExists = Object.values(pick(item, keys)).find((value) =>
      value === 0 ? true : !!value,
    );

    return (
      isActive || item.isCreate || item.isRecentlyCreated || !isNil(isExists)
    );
  });

  const omittedFilter = omit(dataFilter, [
    "worker_id", // Common
    "transfered_by", // Expenses, Works

    // Provisions
    "branch_id",
    "division_id",
    "department_id",
    "provision_type",
    "provision_item_id",
    "contractor",

    //Expenses
    "detail",

    // Transfers
    "type_of_transfer",

    // Works
    "position_id",

    // Staff
    "insurance_existence_last_year",
    ...MONTHLY.map((item) => `insurance_existence_last_${item}_month`),
    "salary_last_year",
    ...MONTHLY.map((item) => `salary_${item}_month`),
    "num_children_last_year",
    ...MONTHLY.map((item) => `num_children_${item}_month`),
  ]);

  if (isEmpty(omittedFilter)) {
    return originalData;
  }

  const condition: any = [];

  // Flatten nested dataFilter structure
  const flattenedFilters = flattenDataFilter(omittedFilter);

  for (const { path, value } of flattenedFilters) {
    // Skip if value is null or not a filter object
    if (value === null || value === undefined) {
      continue;
    }

    // Check if value is a filter object (has search, sort, filter properties)
    if (
      typeof value !== "object" ||
      Array.isArray(value) ||
      (!("search" in value) && !("sort" in value) && !("filter" in value))
    ) {
      continue;
    }

    // Parse key: remove _attr suffix if present
    let parsedKey = path;
    if (path.includes("_attr")) {
      parsedKey = path.replace("_attr", "");
    } else if (path.includes("attr")) {
      // Handle cases like insurance_existence_last_6_month_attr
      const parts = path.split("_");
      if (parts.length >= 2) {
        // Remove last part if it's "attr"
        if (parts[parts.length - 1] === "attr") {
          parsedKey = parts.slice(0, -1).join("_");
        } else {
          // Fallback to old logic for other cases
          parsedKey =
            parts.length === 4
              ? parts.slice(0, 3).join("_")
              : parts.length === 5
              ? parts.slice(0, 4).join("_")
              : parts.length === 2 && path.includes("-")
              ? parts.slice(0, 1).join("_")
              : parts.slice(0, 2).join("_");
        }
      }
    }

    const rawFilter = get(value, "filter");
    let extractedFilter: any[] | undefined = undefined;

    if (rawFilter !== undefined) {
      if (Array.isArray(rawFilter)) {
        extractedFilter = rawFilter;
      } else if (typeof rawFilter === "object" && rawFilter !== null) {
        extractedFilter = Object.keys(rawFilter).filter(
          (k) => (rawFilter as Record<string, any>)[k] === true,
        );
      } else {
        extractedFilter = [rawFilter];
      }
    }

    condition.push({
      key: parsedKey,
      search: get(value, "search", ""),
      sort: get(value, "sort", ""),
      filter: extractedFilter
        ? extractedFilter.map((item: any) => item?.toString())
        : undefined,
      filter_special_empty: get(value, "filter_special_empty"),
    });
  }

  // Search
  const dataSearch = cloneDeepData
    .map((item: any) => ({
      ...item,
      month: item?.month ? Number(item?.month) : item?.month,
    }))
    .filter(function (value: any) {
      // Ensure value is not null/undefined
      if (value == null) {
        return false;
      }

      if (value.isCreate || value.isRecentlyCreated) {
        return true;
      }

      for (let i = 0; i < condition.length; i++) {
        const key = condition[i]["key"];

        const isDateField =
          [
            "transfered_at",
            "start_at",
            "end_at",
            "achieve_start_at",
            "achieve_end_at",
            "join_date",
            "retired_date",
            "health_insurance_get_date",
            "health_insurance_lost_date",
            "employment_insurance_get_date",
            "employment_insurance_lost_date",
            "birthday",
            "name_start_date",
            "onboarding_start_date",
            fieldsDefined.name_start_date,
            fieldsDefined.contract_start_date,
          ].includes(key) ||
          key.includes("date") ||
          key.includes("day");

        const fieldValue = get(value, key);
        const valueStr: string =
          isDateField && fieldValue
            ? isJPDateLabel(fieldValue)
              ? fieldValue
              : moment(fieldValue).format("YYYY年MM月DD日")
            : toString(fieldValue);
        // Search filter
        if (!isEmpty(condition[i]["search"])) {
          if (isEmpty(valueStr)) {
            // If search is not empty but value is empty, exclude this item
            return false;
          }
          if (isDateField && fieldValue) {
            const searchValue = condition[i]["search"].toUpperCase();
            const dateValue = isJPDateLabel(fieldValue)
              ? moment(fieldValue, DATE_TIME_FORMAT.DATE_JP)
              : moment(fieldValue);

            const fullDate = dateValue.format("YYYY年MM月DD日");
            const yearMonth = dateValue.format("YYYY年MM月");
            const yearOnly = dateValue.format("YYYY年");

            const matchesSearch =
              fullDate.includes(searchValue) ||
              yearMonth.includes(searchValue) ||
              yearOnly.includes(searchValue);

            if (!matchesSearch) {
              return false;
            }
          } else {
            const computed =
              getValue && fieldValue != null
                ? getValue(fieldValue, key)
                : valueStr;
            const haystack = (computed ?? valueStr).toString().toUpperCase();
            if (!haystack.includes(condition[i]["search"].toUpperCase())) {
              return false;
            }
          }
        }

        // Filter by values
        if (
          condition[i]["filter"] !== undefined ||
          condition[i]["filter_special_empty"]
        ) {
          const expandedFilter = expandFilterValues(
            key,
            condition[i]["filter"].map((item: any) => item?.toString()),
          );

          // Check if filter contains "special_empty_value"
          const hasSpecialEmpty = condition[i]["filter"].includes(
            "special_empty_value",
          );

          // Remove "special_empty_value" from expandedFilter for normal matching
          const normalFilters = expandedFilter.filter(
            (f: any) => f !== "special_empty_value",
          );

          // Check if current value is empty
          const isValueEmpty =
            valueStr == null ||
            (typeof valueStr !== "boolean" && isEmpty(valueStr));

          if (condition[i]["filter_special_empty"]) {
            // If filter_special_empty is set
            if (isValueEmpty) {
              // Value is empty - keep it
              continue;
            } else if (normalFilters.length > 0) {
              if (isDateField && fieldValue) {
                const matchesDateFilter = normalFilters.some(
                  (filterVal: string) => matchDateFilter(fieldValue, filterVal),
                );

                if (!matchesDateFilter) {
                  return false;
                }
              } else {
                const normalizedDataValues = normalizeDataValue(key, valueStr);
                if (normalizedDataValues.length === 0) {
                  return false;
                }

                const matchesFilter = normalizedDataValues.some((dataVal) =>
                  normalFilters.some((f: any) => dataVal === f),
                );

                if (!matchesFilter) {
                  return false;
                }
              }
            } else {
              // Only filter_special_empty is true, no other filters, but value is not empty - exclude
              return false;
            }
          } else {
            // Normal filter logic
            if (hasSpecialEmpty) {
              // If "special_empty_value" is in filter
              if (isValueEmpty) {
                // Value is empty and filter includes "special_empty_value" - keep it
                continue;
              } else if (normalFilters.length > 0) {
                // Value is not empty - check against other filter values
                if (isDateField && fieldValue) {
                  const matchesDateFilter = normalFilters.some(
                    (filterVal: string) =>
                      matchDateFilter(fieldValue, filterVal),
                  );

                  if (!matchesDateFilter) {
                    return false;
                  }
                } else {
                  // Value is not empty - check against other filter values
                  const normalizedDataValues = normalizeDataValue(
                    key,
                    valueStr,
                  );
                  if (normalizedDataValues.length === 0) {
                    return false;
                  }

                  const matchesFilter = normalizedDataValues.some((dataVal) =>
                    normalFilters.some((f: any) => dataVal === f),
                  );

                  if (!matchesFilter) {
                    return false;
                  }
                }
              } else {
                // Only "special_empty_value" in filter, but value is not empty - exclude
                return false;
              }
            } else {
              // Normal filter without "special_empty_value"
              if (isValueEmpty) {
                return false;
              }

              if (isDateField && fieldValue) {
                const matchesDateFilter = normalFilters.some(
                  (filterVal: string) => matchDateFilter(fieldValue, filterVal),
                );

                if (!matchesDateFilter) {
                  return false;
                }
              } else {
                const normalizedDataValues = normalizeDataValue(key, valueStr);
                if (normalizedDataValues.length === 0) {
                  return false;
                }

                const matchesFilter = normalizedDataValues.some((dataVal) =>
                  normalFilters.some((f: any) => dataVal === f),
                );

                if (!matchesFilter) {
                  return false;
                }
              }
            }
          }
        }
      }
      return true;
    });

  // Sort
  const sortKeys = condition
    .filter((item: any) => item.sort)
    .map((item: any) => item.key);
  const dataSort = condition
    .map((item: any) => item.sort)
    .filter((item: any) => item) as ["asc", "desc"];

  const result = !isEmpty(dataSort)
    ? orderBy(dataSearch, sortKeys, dataSort)
    : dataSearch;

  return result
    .filter((item: any) => item != null) // Ensure no null/undefined items
    .map((item: any) => ({
      ...item,
      month: item?.month ? item?.month?.toString() : item.month,
    }));
};

export const handleTitleDiagLogSalary = (column: string) => {
  switch (column) {
    case "worker_id":
      return "社員";
    case "position_id":
      return "役職";
    case "division_id":
      return "部署";
    case "branch_id":
      return "支店";
    case "department_id":
      return "部門";
    case "provision_type":
      return "分類";
    case "provision_item_id":
      return "項目";
    case "type_of_transfer":
      return "振込種別";
    case "transfered_by":
      return "支払元";
    case "unit":
      return "単位";

    default:
      return "";
  }
};

export const convertDateJp = (dateString: string) => {
  return dateString.replace(/年|月/g, "-").replace("日", "");
};

export const mapping = <T>(array: Array<T>, key: keyof T): Map<keyof T, T> => {
  const map = new Map();

  (array || [])?.forEach((item) => {
    map.set(item[key as keyof T], item);
  });

  return map;
};

export const formatNumber = (number: number) => {
  if (!isNumber(number) || isNaN(number)) {
    return "";
  }

  return new Intl.NumberFormat("en-US").format(number);
};

export const formatDecimal = (
  value: string,
  isFormat: boolean,
  numberOfDecimalPlace?: number,
) => {
  if (!value) return "";
  const [intPart, decimalPart = ""] = value.split(".");

  const formattedIntPart = isFormat
    ? new Intl.NumberFormat("en-US").format(Number(intPart))
    : intPart;

  if (numberOfDecimalPlace == null) {
    return decimalPart
      ? `${formattedIntPart}.${decimalPart}`
      : formattedIntPart;
  }

  return `${formattedIntPart}.${(
    decimalPart + "0".repeat(numberOfDecimalPlace)
  ).slice(0, numberOfDecimalPlace)}`;
};

export const convertFullWidthToHalfWidthNumber = (textNumber: string) => {
  if (!textNumber) return null;

  const halfWidth = textNumber
    .trim()
    .replace(/[０-９]/g, (char) =>
      String.fromCharCode(char.charCodeAt(0) - 0xfee0),
    )
    .replace(/．/g, ".");

  // Giữ lại chỉ số và dấu .
  const cleaned = halfWidth.replace(/[^0-9.]/g, "");

  // Chỉ giữ dấu . đầu tiên
  const firstDot = cleaned.indexOf(".");
  const normalized =
    firstDot === -1
      ? cleaned
      : cleaned.slice(0, firstDot + 1) +
        cleaned.slice(firstDot + 1).replace(/\./g, "");

  return normalized;
};

export const isJSON = (str: any): boolean => {
  if (!isString(str)) {
    return false;
  }
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

export function convertDateToEpochTime(dateStringInput: string) {
  let dateString = dateStringInput;
  if (dateStringInput.includes("年")) {
    dateString = dateStringInput
      .replace("年", "/")
      .replace("月", "/")
      .replace("日", ""); //convert 2020年12月10日  => 2020/12/10
    const [year, month, day] = dateString.split("/");
    return new Date(`${year}-${month}-${day}`).getTime();
  }
  //Check if the date format is "YYYY/MM/DD"
  if (/\d{4}\/\d{2}\/\d{2}/.test(dateString)) {
    const [year, month, day] = dateString.split("/");
    return new Date(`${year}-${month}-${day}`).getTime();
  }
  // Check if the date format is "DD/MM/YYYY"
  else if (/\d{2}\/\d{2}\/\d{4}/.test(dateString)) {
    const [day, month, year] = dateString.split("/");
    return new Date(`${year}-${month}-${day}`).getTime();
  }
  throw new Error("Invalid date format");
}

export function convertEpochToDate(epochTime: string, format = "DD/MM/YYYY") {
  const date = new Date(parseInt(epochTime)); // Convert epoch to date object

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(date.getDate()).padStart(2, "0");

  if (format === "YYYY/MM/DD") {
    return `${year}/${month}/${day}`;
  } else if (format === "DD/MM/YYYY") {
    return `${day}/${month}/${year}`;
  } else {
    throw new Error("Invalid date format");
  }
}

export function isValidEpochTime(epochTime: string) {
  if (isNaN(Number(epochTime))) return false;

  const epoch = parseInt(epochTime, 10); // Parse as integer
  const currentTime = Date.now(); // Current epoch time

  // Check if it's a valid number and within the valid epoch time range
  return !isNaN(epoch) && epoch >= 0 && epoch <= currentTime;
}

export const convertCharterFullWidth = (
  value: string,
  isConvertSpace?: boolean,
) => {
  const result =
    value?.replace(/[\uff01-\uff5e]/g, function (ch) {
      return String.fromCharCode(ch.charCodeAt(0) - 0xfee0);
    }) || "";
  if (isConvertSpace) {
    return result.replace(/\u3000/g, " ");
  }
  return result;
};

export const containsFullWidth = (str: string) => {
  const fullWidthRegex = /[\uFF01-\uFF5E\u3000-\u303F\u4E00-\u9FFF]/;
  return fullWidthRegex.test(str);
};

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const getFileExtentionByUrl = (url: string) =>
  url?.split(".")?.pop()?.toLowerCase() || "";

export const getTypeFile = (urlItem: string) => {
  if (IMAGE_FILE_EXTENSIONS.includes(getFileExtentionByUrl(urlItem || "")))
    return "image";
  else if (VIDEO_FILE_EXTENSIONS.includes(getFileExtentionByUrl(urlItem || "")))
    return "video";
  else if (
    OFFICE_FILE_EXTENSIONS.includes(getFileExtentionByUrl(urlItem || ""))
  )
    return "office";
  return "unknown";
};

export const autoFormatZipcode = (value: string) => {
  const numericValue = value.replace(/-/g, "");
  if (numericValue.length <= 3) {
    return numericValue;
  }
  const firstPart = numericValue.slice(0, 3);
  const secondPart = numericValue.slice(3);
  return `${firstPart}-${secondPart}`;
};

export const getTextWidth = (text: string, font = "15px Noto Sans JP") => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return 0;
  context.font = font;
  return context.measureText(text).width;
};

export const getClipboardData = async () => {
  try {
    if (navigator.clipboard) {
      const text = await navigator.clipboard.readText();
      return text || "";
    }
  } catch (err) {
    return "";
  }
};

export const detectTextOrientation = (clipboardText?: string) => {
  if (!clipboardText) {
    return "";
  }

  const isHorizontal = clipboardText.includes("\t");
  const isVertical = clipboardText.includes("\n");

  if (isHorizontal && !isVertical) {
    return "horizontal";
  } else if (isVertical && !isHorizontal) {
    return "vertical";
  } else if (isVertical && isHorizontal) {
    return "combine";
  } else {
    return "unknown";
  }
};

export const getActionType = async ({
  data,
  actionType,
  changes,
  fieldId,
}: {
  data?: any[];
  changes?: CellChange<
    TextCell | NumberCell | CheckboxCell | DropdownCell | DateCell | any
  >[];
  actionType?: string;
  fieldId?: any;
}) => {
  const textFromClipboard = await getClipboardData();

  if (!data || !changes) return actionType;

  const isSameValueCells = changes?.every(
    (i: any) => i?.newCell?.text === (changes as any)?.[0]?.newCell?.text,
  );

  const isSameRowId = changes?.every(
    (i: any) => i?.rowId === (changes as any)?.[0]?.rowId,
  );
  const isSameColId = changes?.every(
    (i: any) => i?.columnId === (changes as any)?.[0]?.columnId,
  );

  if (!actionType) {
    if (data?.length === 1 && !data?.[0]?.[fieldId] && changes?.length === 1) {
      actionType = "create-a-new-line";
    } else if (
      data?.length === 1 &&
      !data?.[0]?.[fieldId] &&
      changes &&
      changes?.length > 1
    ) {
      if (!detectTextOrientation(textFromClipboard)) {
        actionType = "create-drag-horizontal";
      } else if (detectTextOrientation(textFromClipboard) === "horizontal") {
        actionType = "create-copy-one-line";
      } else if (!isSameValueCells) {
        actionType = "create-drag-vertical-one-line";
      }
    } else if (
      data?.length === 1 &&
      data?.[0]?.[fieldId] &&
      changes?.length === 1
    ) {
      if (String((changes?.[0]?.newCell as any)?.text) === textFromClipboard) {
        actionType = "edit-copy-one-cell-to-one-cell";
      } else {
        actionType = "edit-one-line";
      }
    } else if (data?.length === 1 && changes?.length > 1 && isSameRowId) {
      if (isSameValueCells) {
        if (!changes?.[0]?.newCell?.text) {
          actionType = "delete-delete-all-data-one-line";
        } else {
          actionType = "edit-copy-one-cell-to-multiple-cell-horizontal";
        }
      } else if (detectTextOrientation(textFromClipboard) === "horizontal") {
        actionType = "edit-copy-multiple-cell-to-multiple-cell-horizontal";
      } else {
        actionType = "edit-drag-vertical-one-line";
      }
    } else if (data?.length > 1) {
      const isAllDataIsCreate = data?.every((i) => !i?.[fieldId]);
      const isHaveCreateData = data?.some((i) => !i?.[fieldId]);

      if (isHaveCreateData) {
        if (isAllDataIsCreate) {
          if (
            detectTextOrientation(textFromClipboard) === "combine" ||
            detectTextOrientation(textFromClipboard) === "vertical"
          ) {
            actionType = "create-copy-multiple-line";
          } else {
            actionType = "create-drag-vertical-multiple-line";
          }
        } else {
          if (
            detectTextOrientation(textFromClipboard) === "combine" ||
            detectTextOrientation(textFromClipboard) === "vertical"
          ) {
            actionType = "copy-multiple-line";
          } else {
            actionType = "drag-vertical";
          }
        }
      } else {
        if (isSameValueCells) {
          if (!(changes as any)?.[0]?.newCell?.text) {
            if (isSameColId) {
              actionType = "edit-multiple-line";
            } else {
              actionType = "delete-delete-all-data-multiple-line";
            }
          } else {
            if (textFromClipboard === (changes as any)?.[0]?.newCell?.text) {
              actionType = "edit-copy-one-cell-to-multiple-cell-vertical";
            } else {
              actionType = "edit-drag-vertical-multiple-line";
            }
          }
        } else {
          if (
            isSameColId &&
            detectTextOrientation(textFromClipboard) === "vertical"
          ) {
            actionType = "edit-copy-multiple-cell-to-multiple-cell-vertical";
          } else if (isSameRowId) {
            actionType = "edit-drag-horizontal";
          } else if (isSameColId) {
            if (
              isSameValueCells &&
              textFromClipboard === (changes as any)?.[0]?.newCell?.text
            ) {
              actionType = "edit-copy-one-cell-to-multiple-cell-horizontal";
            } else {
              actionType = "edit-drag-vertical-multiple-line";
            }
          } else {
            if (detectTextOrientation(textFromClipboard) === "combine") {
              actionType =
                "edit-copy-multiple-cell-to-multiple-cell-vertical-horizontal";
            } else {
              actionType = "edit-drag-vertical-horizontal-multiple-line";
            }
          }
        }
      }
    }
  }

  return actionType;
};

export const phoneFullWidthToHalfWidth = (str: string): string => {
  if (!str) return str;

  return str
    .replace(/[！-～]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .replace(/\u3000/g, " ")
    .replace(/[\u30FC\uFF0D\u2015\u2212]/g, "-");
};
export const parseTextFullWidthToHalfWidthNumber = (str: string): string => {
  if (!str) return str;

  return str
    .replace(/[！-～]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .replace(/\u3000/g, " ")
    .replace(/[\u30FC\uFF0D\u2015\u2212]/g, "-")
    .replace(/[^0-9\-,.]/g, "");
};
export const parseTextFullWidthToHalfWidthPositiveNumber = (
  str: string,
): string => {
  if (!str) return str;

  return str
    .replace(/[！-～]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .replace(/\u3000/g, " ")
    .replace(/[\u30FC\uFF0D\u2015\u2212]/g, "-")
    .replace(/[^0-9]/g, "");
};
export const parseFullWidthToHalfWidth = (str: string): string => {
  if (!str) return str;

  return str
    .replace(/[！-～]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .replace(/\u3000/g, " ");
};

export function parseJson<T = any>(json: any, fallback: any): any {
  try {
    return JSON.parse(json);
  } catch (e) {
    return fallback;
  }
}

export const convertImageToBase64 = async (url: string) => {
  const baseUrl = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL?.split("/api/v1")?.[0]
    : "";
  try {
    const response = await fetchProtectedFile(`${baseUrl}${url}`);

    if (!response.ok) throw new Error("Image request failed");

    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result); // this is the base64 string
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error converting image to Base64:", error);
    return null;
  }
};

export function convertBase64ToFile(
  base64: string,
  isMiniFile?: boolean,
  fileName?: string,
) {
  const name = fileName || Date.now().toString();
  const [header, data] = base64.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/png";

  const binary = atob(data);
  const buffer = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    buffer[i] = binary.charCodeAt(i);
  }

  return new File([buffer], name, { type: mime });
}
export async function convertBase64ToMiniFileAsync(
  base64: string,
  fileName?: string,
) {
  const name = fileName?.endsWith(".jpg")
    ? fileName
    : `${fileName || Date.now()}.jpg`;

  const img = new Image();
  img.src = base64;

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Load image failed"));
  });

  const maxWidth = 1024;
  const ratio = Math.min(maxWidth / img.width, 1);

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * ratio);
  canvas.height = Math.round(img.height * ratio);

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context not found");

  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      "image/jpeg",
      0.7,
    );
  });

  return {
    file: new File([blob], name, { type: "image/jpeg" }),
    blob,
  };
}

export function formatStringAppartNumber(
  inputString?: string,
  appPartType?: AppPartType,
  isPerfix = false,
  isSuffix = false,
): string {
  if (!inputString || !appPartType) return "";

  const {
    unit_of_measure_type,
    unit_of_measure = "",
    number_of_decimal_place = 0,
    is_use_thousands_separators,
  } = appPartType;

  const value = Number(inputString);
  if (isNaN(value)) return "";

  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: number_of_decimal_place,
    maximumFractionDigits: number_of_decimal_place,
    useGrouping: !!is_use_thousands_separators,
  }).format(value);

  const prefix = isPerfix
    ? ["frefix", "frefix_suffix"].includes(unit_of_measure_type)
      ? unit_of_measure
      : ""
    : "";
  const suffix = isSuffix
    ? ["suffix", "frefix_suffix"].includes(unit_of_measure_type)
      ? unit_of_measure
      : ""
    : "";

  return `${prefix}${formatted}${suffix}`;
}

export const setPagePageSize = (
  name: string,
  data: {
    page: number;
    pageSize: number;
    id: string;
  },
) => {
  localStorage.setItem(
    "pagePageSize",
    JSON.stringify({
      [name]: data,
    }),
  );
};

export const resetPagePageSize = () => {
  localStorage.setItem("pagePageSize", "{}");
};

export const getPagePageSize = (name: string, id?: string | string) => {
  const dataJson = localStorage.getItem("pagePageSize") || "{}";
  const data = JSON.parse(dataJson);
  return data[name]?.id == id ? data[name] : {};
};

export const isColor = (str?: string) => {
  if (!str) return false;
  const s = new Option().style;
  s.color = str;
  return s.color !== "";
};

export async function fetchFileToFileAndBlob(
  uri: string,
): Promise<[File, Blob]> {
  const response = await fetchProtectedFile(uri);

  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  }

  const blob = await response.blob();
  const file = new File([blob], "fetched.xlsx", { type: blob.type });
  return [file, blob];
}

//get post code japan . plz refer at japan-postal-code for more detail

const JSONDATA = "https://yubinbango.github.io/yubinbango-data/data";

const PREFMAP = [
  null, //0
  "北海道", //1
  "青森県", //2
  "岩手県", //3
  "宮城県", //4
  "秋田県", //5
  "山形県", //6
  "福島県", //7
  "茨城県", //8
  "栃木県", //9
  "群馬県", //10
  "埼玉県", //11
  "千葉県", //12
  "東京都", //13
  "神奈川県", //14
  "新潟県", //15
  "富山県", //16
  "石川県", //17
  "福井県", //18
  "山梨県", //19
  "長野県", //20
  "岐阜県", //21
  "静岡県", //22
  "愛知県", //23
  "三重県", //24
  "滋賀県", //25
  "京都府", //26
  "大阪府", //27
  "兵庫県", //28
  "奈良県", //29
  "和歌山県", //30
  "鳥取県", //31
  "島根県", //32
  "岡山県", //33
  "広島県", //34
  "山口県", //35
  "徳島県", //36
  "香川県", //37
  "愛媛県", //38
  "高知県", //39
  "福岡県", //40
  "佐賀県", //41
  "長崎県", //42
  "熊本県", //43
  "大分県", //44
  "宮崎県", //45
  "鹿児島県", //46
  "沖縄県", //47
];

const CACHE: Record<string, any> = {};

export async function getAddressByZipcode(zipcode: string) {
  function parseData(nzip: string, data: Record<string, any>) {
    const array = data[nzip];

    if (!array) {
      return {
        code: "zip_code_doesnt_existed",
        data: null,
      };
    }

    const pref_id = array[0];
    if (!pref_id || !PREFMAP[pref_id]) {
      return {
        code: "zip_code_doesnt_existed",
        data: null,
      };
    }

    return {
      code: "success",
      data: {
        prefecture: PREFMAP[pref_id],
        prefecture_code: array[0] || "",
        city: array[1] || "",
        area: array[2] || "",
        street: array[3] || "",
        fullAddress: (array[1] || "") + (array[2] || "") + (array[3] || ""),
      },
    };
  }
  try {
    const nzip = (zipcode || "").replace(/\D/g, "");
    if (!REGEX.ZIP_CODE.test(zipcode)) {
      return {
        code: "zip_code_is_invalid",
        data: null,
      };
    }

    const zip3 = nzip.slice(0, 3);

    // cache data
    if (CACHE[zip3]) {
      return {
        code: "success",
        data: parseData(nzip, CACHE[zip3]),
      };
    }

    const jsUrl = `${JSONDATA}/${zip3}.js`;
    const jsCode = await fetch(jsUrl).then((r) => {
      if (!r.ok) throw new Error("郵便番号データを取得できませんでした。");
      return r.text();
    });

    const jsonPart = jsCode.replace(/^\$yubin\(|\);\s*$/g, "");
    const data = JSON.parse(jsonPart);

    CACHE[zip3] = data;

    return {
      code: "success",
      data: parseData(nzip, data),
    };
  } catch (e: any) {
    return { code: "error", data: null };
  }
}

export const isMacOS = (): boolean => {
  return (
    /Mac|iPhone|iPod|iPad/i.test(navigator.platform) ||
    /Mac OS X/i.test(navigator.userAgent)
  );
};

const mapWeekdayIndexToJP = ["日", "月", "火", "水", "木", "金", "土"] as const;

export const getDaysOfWeek = (day: Dayjs = dayjs()) => {
  const baseDay = day.isValid() ? day : dayjs();
  const dow = baseDay.day();
  const daysFromMonday = dow === 0 ? 6 : dow - 1;
  const startOfWeek = baseDay.subtract(daysFromMonday, "day");

  return Array.from({ length: 7 }).map((_, i) => {
    const d = startOfWeek.add(i, "day");
    const weekdayShort = d.format("dd"); // Mo, Tu, We...
    const weekdayIndex = d.day();
    const weekdayJP =
      Number.isInteger(weekdayIndex) &&
      weekdayIndex >= 0 &&
      weekdayIndex < mapWeekdayIndexToJP.length
        ? mapWeekdayIndexToJP[weekdayIndex]
        : "";

    return {
      date: d.format(DATE_TIME_FORMAT.DATE_DEFAULT),
      day: d.date(),
      weekday: weekdayShort, // ex: "Mo"
      weekdayFull: d.format("dddd"), // ex:  "月曜日"
      weekdayJP,
    };
  });
};

export const stripHtml = (html?: string) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};
export { validationRules } from "./validationRules";

export function formatToYMD(
  input: string | number | Date,
  tz = "Asia/Tokyo",
): string | null {
  if (!input) return null;

  const formats = [
    DATE_TIME_FORMAT.DATE_TIME,
    DATE_TIME_FORMAT.DATE,
    DATE_TIME_FORMAT.DATE_DEFAULT,
    DATE_TIME_FORMAT.DATE_JP,
    DATE_TIME_FORMAT.DATE_JP_MD,
    DATE_TIME_FORMAT.DATE_TIME_JP,
    DATE_TIME_FORMAT.DATE_JP_TIME,
    DATE_TIME_FORMAT.DATE_QUERY,
    DATE_TIME_FORMAT.DATE_MONTH_JP,
    DATE_TIME_FORMAT.DATE_TIME_INPUT,
    DATE_TIME_FORMAT.DATE_SLASH_DMY,
    DATE_TIME_FORMAT.DATE_DASH,
    DATE_TIME_FORMAT.DATE_DEFAULT,
    DATE_TIME_FORMAT.DATE_JP_SLASH,
    DATE_TIME_FORMAT.DATE_JP_DOT,
  ];

  let d: dayjs.Dayjs;

  if (typeof input === "number" || input instanceof Date) {
    d = dayjs(input).tz(tz);
  } else {
    d = dayjs(input, formats, true).tz(tz);
  }

  if (!d.isValid()) return null;

  return d.format("YYYY-MM-DD");
}

export function formatToJPDate(
  date?: string | null,
  isFormatMonth?: boolean,
): string {
  const formats = [
    DATE_TIME_FORMAT.DATE_TIME,
    DATE_TIME_FORMAT.DATE,
    DATE_TIME_FORMAT.DATE_DEFAULT,
    DATE_TIME_FORMAT.DATE_JP,
    DATE_TIME_FORMAT.DATE_TIME_JP,
    DATE_TIME_FORMAT.DATE_JP_TIME,
    DATE_TIME_FORMAT.DATE_QUERY,
    DATE_TIME_FORMAT.DATE_MONTH_JP,
    DATE_TIME_FORMAT.DATE_TIME_INPUT,
    DATE_TIME_FORMAT.DATE_SLASH_DMY,
    DATE_TIME_FORMAT.DATE_DASH,
    DATE_TIME_FORMAT.DATE_DEFAULT,
    DATE_TIME_FORMAT.DATE_JP_SLASH,
    DATE_TIME_FORMAT.DATE_JP_DOT,
    DATE_TIME_FORMAT.DATE_JP_WEEK,
    DATE_TIME_FORMAT.DATE_JP_MD,
    DATE_TIME_FORMAT.DATE_JP_MD_WEEK,
    DATE_TIME_FORMAT.DATE_TIME_JP_MD,
    DATE_TIME_FORMAT.DATE_JP_MD_TIME,
    DATE_TIME_FORMAT.DATE_MONTH_JP_M,
  ];
  if (!date) return "";
  const m = moment(date, formats, true);
  if (!m.isValid()) return "";

  return isFormatMonth ? m.format("YYYY年MM月") : m.format("YYYY年MM月DD日");
}

// Support multiple JP date formats: YYYY年, YYYY年MM月, YYYY年MM月DD日, YYYY年MM月DD日 HH:mm:ss
const JP_DATE_REGEX =
  /^\d{4}年((0[1-9]|1[0-2])月((0[1-9]|[12]\d|3[01])日)?)?(\s\d{1,2}:\d{1,2}(:\d{1,2})?)?$/;

export const isJPDateLabel = (text?: string | null): boolean =>
  !!text && JP_DATE_REGEX.test(text);

export const getValidSelectionValue = (
  value: any,
  options: { value: string | number }[],
) => {
  if (!value) return undefined;
  return options?.find((opt) => String(opt.value) === String(value))
    ? value
    : undefined;
};

export const getDisplayLength = (list?: any[]) => {
  return list?.length || "";
};

function parseCsvText(text: string): string[][] {
  const normalized = text.startsWith("\uFEFF") ? text.slice(1) : text;
  if (!normalized.trim()) return [];

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let i = 0;
  let inQuotes = false;

  while (i < normalized.length) {
    const c = normalized[i];

    if (inQuotes) {
      if (c === '"') {
        if (normalized[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += c;
      i += 1;
      continue;
    }

    if (c === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }

    if (c === ",") {
      row.push(cell);
      cell = "";
      i += 1;
      continue;
    }

    if (c === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      i += 1;
      continue;
    }

    if (c === "\r") {
      i += 1;
      continue;
    }

    cell += c;
    i += 1;
  }

  row.push(cell);
  rows.push(row);

  while (rows.length > 0 && rows[rows.length - 1].every((c) => c === "")) {
    rows.pop();
  }

  return rows;
}

export function readCsvFile(file: File): Promise<string[][]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = (e.target?.result as string) ?? "";
        resolve(parseCsvText(text));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("File read failed"));
    reader.readAsText(file, "UTF-8");
  });
}

/** First sheet only; each row → string cells (same shape as {@link readCsvFile}). */
export function readExcelFile(file: File): Promise<string[][]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      void (async () => {
        try {
          const XLSX = await import("xlsx");
          const data = e.target?.result;
          if (!data) {
            resolve([]);
            return;
          }
          const workbook = XLSX.read(new Uint8Array(data as ArrayBuffer), {
            type: "array",
          });
          const sheetName = workbook.SheetNames[0];
          if (!sheetName) {
            resolve([]);
            return;
          }
          const worksheet = workbook.Sheets[sheetName];
          if (!worksheet["!ref"]) {
            resolve([]);
            return;
          }
          const rawRows = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
            raw: false,
            blankrows: false,
          }) as unknown[][];
          const normalized = rawRows.map((row) =>
            (row || []).map((cell) =>
              cell === null || cell === undefined ? "" : String(cell),
            ),
          );
          while (
            normalized.length > 0 &&
            normalized[normalized.length - 1].every((c) => c === "")
          ) {
            normalized.pop();
          }
          resolve(normalized);
        } catch (err) {
          reject(err);
        }
      })();
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("File read failed"));
    reader.readAsArrayBuffer(file);
  });
}

export function isSpreadsheetCsvFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    name.endsWith(".csv") ||
    file.type === "text/csv" ||
    file.type === "application/csv"
  );
}

export function isSpreadsheetExcelFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    name.endsWith(".xlsx") ||
    name.endsWith(".xls") ||
    name.endsWith(".xlsm") ||
    file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    file.type === "application/vnd.ms-excel"
  );
}

export async function readSpreadsheetFile(file: File): Promise<string[][]> {
  if (isSpreadsheetCsvFile(file)) return readCsvFile(file);
  if (isSpreadsheetExcelFile(file)) return readExcelFile(file);
  throw new Error("Unsupported spreadsheet type");
}

export function secondsToHMS(totalSeconds: number) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    hours,
    minutes,
    seconds,
  };
}

export const padString = (value: number | string, numberPad?: number) =>
  String(value).padStart(numberPad || 2, "0");

export const clearSortFromOtherColumns = (
  filter: Record<string, any>,
  sortedColumnId: string,
): Record<string, any> => {
  const newFilter = { ...filter };

  Object.keys(newFilter).forEach((columnId) => {
    if (columnId !== sortedColumnId && newFilter[columnId]?.sort) {
      const cleanedFilter = { ...newFilter[columnId] };
      delete cleanedFilter.sort;
      if (Object.keys(cleanedFilter).length > 0) {
        newFilter[columnId] = cleanedFilter;
      } else {
        delete newFilter[columnId];
      }
    }
  });

  return newFilter;
};

/**
 * Converts mixed half-width/full-width text to full-width text.
 * Half-width Katakana is normalized before ASCII characters are converted.
 */
export {
  DATE_PICKER_PARSE_FORMATS,
  DATE_PICKER_SAVE_FORMAT,
  formatDatePickerSaveValue,
  isCompleteDatePickerText,
  isDatePickerOnOrAfter,
  parseDatePickerInput,
  parseDateTimePickerInput,
  DATE_TIME_PICKER_PARSE_FORMATS,
  toDatePickerApiDate,
  yupDatePicker,
} from "./datePickerValue";

export const toFullWidth = (value: string | number): string =>
  String(value ?? "")
    .replace(/[\uFF61-\uFF9F]+/g, (text) => text.normalize("NFKC"))
    .replace(/[\u0020-\u007E]/g, (character) =>
      character === " "
        ? "\u3000"
        : String.fromCharCode(character.charCodeAt(0) + 0xfee0),
    );
